<template>
  <div>
    <template v-if="sortedResults.length">
      <ul class="list-reset result-list">
        <li
          v-for="(resort, index) in resortChunks[page - 1]"
          :key="resort.objectID"
        >
          <responsive>
            <template #lgmd>
              <loading-item v-if="isLoading" />
              <template v-else>
                <resort-item-large :resort="resort" />
                <contact-block v-if="sortedResults.length > 3 && index===2" product="resort"/>
              </template>
            </template>
            <template #sm>
              <loading-item-mobile v-if="isLoading" />
              <resort-item-small v-else :resort="resort" />
              <contact-block v-if="sortedResults.length > 3 && index===2" product="resort" />
            </template>
          </responsive>

          <hr
            class="separator-slim my-10"
            v-if="index !== resortChunks[page - 1].length - 1"
          />
        </li>
      </ul>

      <responsive>
        <template #lg>
          <div class="flex items-center my-10">
            <div class="flex-1">
              <pagination
                @click="onSetPage"
                :pagination-info="paginationInfo"
              />
            </div>

            <div class="flex-none">
              {{ paginationStatus }}
            </div>
          </div>
        </template>
        <template #mdsm>
          <mobile-pagination
            class="sm:px-6"
            :pagination-info="paginationInfo"
          />
        </template>
      </responsive>
    </template>

    <template v-else-if="isLoading === true">
      <ul class="list-reset lg:mt-24">
        <li v-for="index in 10" :key="index">
          <responsive>
            <template #lgmd>
              <loading-item v-if="isLoading" />
            </template>
            <template #sm>
              <loading-item-mobile v-if="isLoading" />
            </template>
          </responsive>

          <hr class="separator-slim my-10" v-if="index !== 10" />
        </li>
      </ul>
    </template>

    <template v-else-if="hasNoResult">
      <div class="container-sm text-center">
        <h3 class="fs-24 font-medium mb-6">
          We haven't found any resorts matching your criteria.
        </h3>

        <div class="inline-flex gap-4 sm:w-full sm:flex-wrap">
          <a
            v-if="productAvailability.hasLiveaboards"
            :href="liveaboardURL"
            class="sm:w-full"
          >
            <button type="button" class="btn btn-secondary w-full">
              View liveaboards
            </button>
          </a>
          <a
            v-if="productAvailability.hasEcoventures"
            :href="ecoventureURL"
            class="sm:w-full"
          >
            <button type="button" class="btn btn-secondary w-full">
              View ecoventures
            </button>
          </a>
          <a
            v-if="productAvailability.hasYachts"
            :href="yachtURL"
            class="sm:w-full"
          >
            <button type="button" class="btn btn-secondary w-full">
              View yachts
            </button>
          </a>
        </div>
      </div>

      <speak-with-us class="mt-10" />
    </template>
  </div>
</template>

<script>
import chunk from "lodash/chunk";
import Pagination from "../../components/core/Pagination";
import Responsive from "../../components/core/Responsive";
import CustomSelect from "../../components/core/CustomSelect";
import SpeakWithUs from "../../components/Search/SpeakWithUs";
import ResortItemLarge from "./ResortItemLarge";
import LoadingItem from "./LoadingItem";
import LoadingItemMobile from "./LoadingItemMobile";
import ResortItemSmall from "./ResortItemSmall";
import MobilePagination from "./MobilePagination";
import ContactBlock from "./ContactBlock";
import { mapState, mapMutations, mapGetters } from "vuex";
import { convertToOtherSearchProduct } from "../../helpers/searchHelpers";

const PER_PAGE = 25;

export default {
  components: {
    Pagination,
    Responsive,
    ResortItemLarge,
    LoadingItem,
    ResortItemSmall,
    MobilePagination,
    CustomSelect,
    SpeakWithUs,
    LoadingItemMobile,
    ContactBlock,
  },
  data() {
    return {
      sortOptions: [
        { label: "Our Top Picks", value: "top-pick" },
        { label: "Price (lowest first)", value: "price" },
        { label: "Top Rated", value: "top-rated" },
      ],
      productAvailability: {},
    };
  },
  computed: {
    ...mapState(["destination", "ranking", "page", "statistics", "isLoading"]),
    ...mapGetters(["sortedResults"]),
    sortBy: {
      get() {
        return this.ranking;
      },
      set(val) {
        this.setRanking(val);
        this.setPage(1);
      },
    },
    resortChunks() {
      return chunk(this.sortedResults, PER_PAGE);
    },
    paginationInfo() {
      return {
        page: this.page,
        per_page: PER_PAGE,
        total: this.sortedResults.length,
      };
    },
    paginationStatus() {
      const { page, per_page, total } = this.paginationInfo;
      const from = (page - 1) * per_page + 1;
      const to = Math.min(page * per_page, total);

      return `Showing ${from} to ${to} of ${total}`;
    },
    hasNoResult() {
      return (
        Boolean(this.statistics) &&
        this.sortedResults.length === 0 &&
        this.isLoading === false
      );
    },
    liveaboardURL() {
      return convertToOtherSearchProduct("resort", "liveaboard");
    },
    ecoventureURL() {
      return convertToOtherSearchProduct("resort", "ecoventure");
    },
    yachtURL() {
      return convertToOtherSearchProduct("resort", "yacht");
    },
  },
  mounted() {
    this.$store.dispatch("loadSuppliers");
    this.$store.dispatch("loadStatistics");

    if (window.zubluDestination) {
      const url = `${window.ZUBLU_VARIABLES.ODYSSEY_API}/api/v2/products_availability_query`;
      const payload = {
        destination_structure: window.zubluDestination,
      };

      $.post(url, payload).then((response) => {
        this.productAvailability = response;
      });
    }
  },
  methods: {
    ...mapMutations(["setRanking", "setPage"]),
    onSetPage(p) {
      this.$store.commit("setPage", p);
      document.querySelector(".search-result").scrollIntoView({
        behavior: "smooth",
      });
    },
    handleClearFitlers() {
      this.$store.commit("clearFilters");
      this.$store.dispatch("loadSuppliers");
    },
  },
};
</script>
